import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  IconButton,
  Container,
  Grid,
  Divider,
  Avatar,
  AppBar,
  Toolbar,
  Paper,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack,
  ShoppingCart,
  Add,
  Remove,
  AccessTime,
  Male,
  Female,
  Star,
  Store,
  ContentCut,
  Business,
  Person,
  Share,
  Favorite,
  FavoriteBorder
} from '@mui/icons-material';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContextNew';
import BottomNavigation from '../components/BottomNavigation';
import BrandLoader from '../components/BrandLoader';
import { serviceAPI, providerAPI, favoritesAPI, homeAPI } from '../services/api';
import useResponsive from '../hooks/useResponsive';
import { formatCurrency, getCountryCode } from '../utils/countryUtils';

const ServiceDetails = () => {
  const { serviceId, providerId } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart, getItemQuantity, totalItems, removeFromCart, updateQuantity, updateItemQuantity, items } = useCart();
  const { isGuest } = useAuth();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  
  const [service, setService] = useState(null);
  const [provider, setProvider] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteId, setFavoriteId] = useState(null);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [restrictedFeature, setRestrictedFeature] = useState('');

  // Mock data for fallback (moved to useEffect)

  useEffect(() => {
    const fetchServiceDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get user location for distance calculation
        const savedLocation = localStorage.getItem('userLocation');
        const savedAddress = localStorage.getItem('userAddress');
        let locationParams = {};
        if (savedLocation) {
          try {
            const locationData = JSON.parse(savedLocation);
            if (locationData.latitude && locationData.longitude) {
              locationParams = {
                latitude: locationData.latitude,
                longitude: locationData.longitude,
                address: savedAddress
              };
            }
          } catch (error) {
            console.error('Error parsing saved location:', error);
          }
        }
        
        // Fetch service and provider data concurrently
        const [serviceResult, providerResult] = await Promise.allSettled([
          serviceAPI.getServiceDetails(serviceId, providerId),
          homeAPI.getAllData(locationParams) // Use same API as home page for real distance calculation
        ]);
        
        // Handle service data
        if (serviceResult.status === 'fulfilled') {
          // The provider-routes returns service data directly in the response
          const serviceData = serviceResult.value.data || serviceResult.value;
          
          
          setService(serviceData);
        } else {
          setService(null);
        }
        
        // Handle provider data from home API
        if (providerResult.status === 'fulfilled') {
          // Extract provider data from home API response
          const homeData = providerResult.value.data || providerResult.value;
          
          // Find the specific provider from the providers array
          const providers = homeData.providers || [];
          const providerData = providers.find(p => p._id === providerId || p.id === providerId);
          
          if (providerData) {
            setProvider(providerData);
          } else {
            // Fallback to provider API if not found in home data
            try {
              const fallbackResult = await providerAPI.getProviderAllData(providerId, locationParams);
              const fallbackData = fallbackResult.data || fallbackResult;
              setProvider(fallbackData);
            } catch (fallbackError) {
              setProvider(null);
            }
          }
        } else {
          setProvider(null);
        }
        
      } catch (error) {
        console.error('Error fetching service details:', error);
        setError('Failed to load service details. Please try again.');
        setService(null);
        setProvider(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchServiceDetails();
    checkFavoriteStatus();
  }, [serviceId, providerId]);
  
  // Refetch when user location changes
  useEffect(() => {
    const handleLocationChange = () => {
      const fetchServiceDetails = async () => {
        try {
          setLoading(true);
          setError(null);
          
          // Get user location for distance calculation
          const savedLocation = localStorage.getItem('userLocation');
          const savedAddress = localStorage.getItem('userAddress');
          let locationParams = {};
          if (savedLocation) {
            try {
              const locationData = JSON.parse(savedLocation);
              if (locationData.latitude && locationData.longitude) {
                locationParams = {
                  latitude: locationData.latitude,
                  longitude: locationData.longitude,
                  address: savedAddress
                };
              }
            } catch (error) {
              console.error('Error parsing saved location:', error);
            }
          }
          
          // Fetch provider data with new location using home API
          const homeResult = await homeAPI.getAllData(locationParams);
          if (homeResult.data) {
            const homeData = homeResult.data;
            const providers = homeData.providers || [];
            const providerData = providers.find(p => p._id === providerId || p.id === providerId);
            
            if (providerData) {
           //   console.log('✅ Provider data updated with new distance:', providerData);
          
              setProvider(providerData);
            } else {
              console.log('❌ Provider not found in updated home data');
            }
          }
        } catch (error) {
          console.error('Error refetching service details:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchServiceDetails();
    };
    
    window.addEventListener('locationUpdated', handleLocationChange);
    return () => window.removeEventListener('locationUpdated', handleLocationChange);
  }, [providerId]);

  const checkFavoriteStatus = async () => {
    try {
      console.log('🔍 [FAVORITES] Checking favorite status for service:', serviceId);
      const response = await favoritesAPI.checkFavoriteStatus(serviceId, 'service');
    //  console.log('🔍 [FAVORITES] Favorite status response:', response);
      if (response.success) {
        setIsFavorite(response.data.isFavorite);
        setFavoriteId(response.data.favoriteId);
        console.log('🔍 [FAVORITES] Set favorite state:', response.data.isFavorite, 'ID:', response.data.favoriteId);
      }
    } catch (error) {
      console.error('❌ [FAVORITES] Error checking favorite status:', error);
    }
  };

  // Guest mode handlers
  const handleGuestRestriction = (featureName) => {
    setRestrictedFeature(featureName);
    setShowSignUpPrompt(true);
  };

  const handleAddToCart = async () => {
    if (isGuest) {
      handleGuestRestriction('add to cart');
      return;
    }
    
    try {
    if (service) {
      const serviceWithOptions = {
        ...service,
        id: service._id || service.id, // Add id field for cart context
        quantity,
        providerName: provider.businessName || provider.name || 'Provider',
        itemType: 'service',
        providerId: provider._id || provider.id,
        // Add combo service information
        isCombo: service.isCombo || false,
        selectedServices: service.selectedServices || [],
        comboServiceNames: service.selectedServices || [],
        // Include detailed service information for availability system
        comboServiceDetails: service.isCombo && service.selectedServices ? 
          service.selectedServices.map(serviceName => ({
            id: serviceName, // This will be the service name since it's already resolved
            name: serviceName,
            serviceName: serviceName,
            duration: null, // Duration not available in ServiceDetails context
            price: null,
            color: '#ff9800',
            image: '',
            category: 'General'
          })) : []
      };
        await addToCart(serviceWithOptions);
      setShowSuccessMessage(true);
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Optionally show an error message to the user
    }
  };

  const handleQuantityChange = async (newQuantity) => {
    if (isGuest) {
      handleGuestRestriction('update cart');
      return;
    }
    
    try {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
      // Update cart with new quantity
      const serviceWithOptions = {
        ...service,
        id: service._id || service.id, // Add id field for cart context
        quantity: newQuantity,
        providerName: provider.businessName || provider.name || 'Provider',
        itemType: 'service',
        providerId: provider._id || provider.id,
        // Add combo service information
        isCombo: service.isCombo || false,
        selectedServices: service.selectedServices || [],
        comboServiceNames: service.selectedServices || [],
        // Include detailed service information for availability system
        comboServiceDetails: service.isCombo && service.selectedServices ? 
          service.selectedServices.map(serviceName => ({
            id: serviceName, // This will be the service name since it's already resolved
            name: serviceName,
            serviceName: serviceName,
            duration: null, // Duration not available in ServiceDetails context
            price: null,
            color: '#ff9800',
            image: '',
            category: 'General'
          })) : []
      };
        
        // Use updateItemQuantity for existing items, addToCart for new items
        if (cartItemExists) {
          await updateItemQuantity(serviceWithOptions, newQuantity);
        } else {
          await addToCart(serviceWithOptions);
        }
    } else if (newQuantity <= 0) {
      // Remove from cart if quantity goes to 0 or below
        const cartItem = items.find(item => item.id === (service._id || service.id) && item.itemType === 'service' && item.providerId === (provider._id || provider.id));
        if (cartItem) {
          await removeFromCart(cartItem.cartItemId);
        }
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      // Optionally show an error message to the user
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${service?.name || service?.serviceName} - ${provider?.name || provider?.businessName}`,
        text: `Check out ${service?.name || service?.serviceName} at ${provider?.name || provider?.businessName} on NIStylez!`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setShowSuccessMessage(true);
    }
  };

  const toggleFavorite = async () => {
    if (favoriteLoading) return;
    
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        // Remove from favorites
        console.log('🗑️ [FAVORITES] Removing from favorites, favoriteId:', favoriteId);
        if (favoriteId) {
          const response = await favoritesAPI.removeFromFavorites(favoriteId);
          console.log('🗑️ [FAVORITES] Remove response:', response);
          setIsFavorite(false);
          setFavoriteId(null);
        }
      } else {
        // Add to favorites
        console.log('➕ [FAVORITES] Adding to favorites, serviceId:', serviceId);
        const response = await favoritesAPI.addToFavorites(serviceId, 'service');
        console.log('➕ [FAVORITES] Add response:', response);
        if (response.success) {
          setIsFavorite(true);
          setFavoriteId(response.data.favorite.id);
          console.log('➕ [FAVORITES] Set favorite state: true, ID:', response.data.favorite.id);
        }
      }
    } catch (error) {
      console.error('❌ [FAVORITES] Error toggling favorite:', error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const renderStars = (rating) => {
    const displayRating = rating || 5; // Show 5 stars if no rating
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        sx={{
          color: index < Math.floor(displayRating) ? '#ffd700' : '#e0e0e0',
          fontSize: 16
        }}
      />
    ));
  };

  const getProviderIcon = (provider) => {
    // Match ProviderProfile logic: if type is 'Store', check category/providerType
    if (provider?.type === 'Store') {
      const category = provider?.category || provider?.providerType;
      if (category === 'salon') {
        return <Business sx={{ color: 'primary.main', fontSize: 20 }} />; // Salon uses Business icon
      } else {
        return <Person sx={{ color: 'primary.main', fontSize: 20 }} />; // Pros uses Person icon
      }
    }
    
    const providerType = provider?.type || provider?.providerType || provider?.businessType;
    switch (providerType?.toLowerCase()) {
      case 'salon':
        return <Business sx={{ color: 'primary.main', fontSize: 20 }} />;
      case 'pros':
      case 'professional':
        return <Person sx={{ color: 'primary.main', fontSize: 20 }} />;
      default:
        return <Business sx={{ color: 'primary.main', fontSize: 20 }} />; // Default to salon (Business icon)
    }
  };

  const renderGenderIcons = (gender) => {
    if (!gender) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Male sx={{ fontSize: 24, color: '#1976d2' }} />
          <Female sx={{ fontSize: 24, color: '#e91e63' }} />
        </Box>
      );
    }
    
    switch (gender.toLowerCase()) {
      case 'male':
        return <Male sx={{ fontSize: 24, color: '#1976d2' }} />;
      case 'female':
        return <Female sx={{ fontSize: 24, color: '#e91e63' }} />;
      case 'both':
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Male sx={{ fontSize: 24, color: '#1976d2' }} />
            <Female sx={{ fontSize: 24, color: '#e91e63' }} />
          </Box>
        );
      default:
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Male sx={{ fontSize: 24, color: '#1976d2' }} />
            <Female sx={{ fontSize: 24, color: '#e91e63' }} />
          </Box>
        );
    }
  };

  const calculateDiscountPercentage = (originalPrice, currentPrice) => {
    if (!originalPrice || !currentPrice) return 0;
    return Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <BrandLoader />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ bgcolor: '#1976d2' }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // Data not found state
  if (!service || !provider) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  const cartItemExists = service ? items.some(item => 
    item.id === (service._id || service.id) && 
    item.itemType === 'service' && 
    item.providerId === (provider._id || provider.id)
  ) : false;
  const cartQuantity = service ? (items.find(item => 
    item.id === (service._id || service.id) && 
    item.itemType === 'service' && 
    item.providerId === (provider._id || provider.id)
  )?.quantity || 0) : 0;

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5', pb: 12 }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#1a237e' }}>
        <Toolbar>
          <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Service Details
          </Typography>
          <IconButton onClick={handleShare} sx={{ color: 'white', mr: 1 }}>
            <Share />
          </IconButton>
          <IconButton onClick={toggleFavorite} sx={{ color: 'white' }} disabled={favoriteLoading}>
            {favoriteLoading ? <BrandLoader size="small" showText={false} /> : (isFavorite ? <Favorite sx={{ color: '#ff6b35' }} /> : <FavoriteBorder />)}
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Service Image */}
      <Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: { xs: 250, sm: 300, md: 350 },
        overflow: 'hidden',
        backgroundColor: '#f5f5f5'
      }}>
        <CardMedia
          component="img"
          image={service.image || service.serviceImage}
          alt={service.name || service.serviceName}
          sx={{ 
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            backgroundColor: '#f8f8f8'
          }}
          onError={(e) => {
            // Fallback for broken images
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        {/* Fallback for broken images */}
        <Box
          sx={{
            display: 'none',
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f0f0f0',
            color: '#666'
          }}
        >
          <Typography variant="h6" sx={{ textAlign: 'center' }}>
            {service.name || service.serviceName}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: -2, position: 'relative', zIndex: 3 
        }}>
        {/* Provider Info - Moved lower */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 25px rgba(0,0,0,0.1)', mb: 3, mt: 4 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                src={provider.profilePhotoUrl || provider.logoUrl || provider.businessImage || provider.profileImagePath || provider.photoURL || provider.image || ''} 
                sx={{ 
                  width: 60, 
                  height: 60,
                  border: '2px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                {/* Fallback: Show first letter of business name if no image */}
                {!provider.profilePhotoUrl && !provider.logoUrl && !provider.businessImage && !provider.profileImagePath && (provider.businessName || provider.name) ? 
                  (provider.businessName || provider.name).charAt(0).toUpperCase() : null
                }
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                  {provider.name || provider.businessName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  📍 {(() => {
                    // Check if we have a valid distance from backend
                    if (provider.distance && provider.distance !== 'Distance unavailable') {
                      return provider.distance.includes('km') ? provider.distance : `${provider.distance} km`;
                    }
                    
                    // Fallback: try to calculate distance if we have location data
                    const savedLocation = localStorage.getItem('userLocation');
                    if (savedLocation && provider.latitude && provider.longitude) {
                      try {
                        const userLocation = JSON.parse(savedLocation);
                        if (userLocation.latitude && userLocation.longitude) {
                          // Calculate distance using Haversine formula
                          const R = 6371; // Earth's radius in km
                          const dLat = (provider.latitude - userLocation.latitude) * Math.PI / 180;
                          const dLon = (provider.longitude - userLocation.longitude) * Math.PI / 180;
                          const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                            Math.cos(userLocation.latitude * Math.PI / 180) * Math.cos(provider.latitude * Math.PI / 180) *
                            Math.sin(dLon/2) * Math.sin(dLon/2);
                          const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                          const distance = R * c;
                          return `${distance.toFixed(1)} km`;
                        }
                      } catch (error) {
                      }
                    }
                    
                    // Final fallback
                    return 'Distance unavailable';
                  })()} away
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getProviderIcon(provider)}
                <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'medium' }}>
                  {provider.type === 'Store' ? (provider.category === 'salon' ? 'Salon' : 'Pros') : provider.type || provider.providerType || provider.businessType || 'Salon'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Service Details */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 25px rgba(0,0,0,0.1)', mb: 3 }}>
          <CardContent sx={{ p: 3 }}>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', flex: 1 }}>
                {service.name || service.serviceName}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, ml: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {renderGenderIcons(service.gender)}
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                    {service.gender === 'Both' ? 'Male & Female' : service.gender}
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime sx={{ color: '#1976d2', fontSize: 18 }} />
                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                  {service.duration || 60} min
                </Typography>
              </Box>
              {service.category && <Chip label={service.category} color="primary" size="small" />}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 'bold', fontSize: '2.5rem' }}>
                  {formatCurrency(service.price || 0)}
                </Typography>
                {service.originalPrice && service.originalPrice > service.price && (
                  <Chip
                    label={`${calculateDiscountPercentage(service.originalPrice, service.price)}% OFF`}
                    sx={{
                      backgroundColor: '#ff6b35',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      height: 32
                    }}
                  />
                )}
              </Box>
              {service.originalPrice && service.originalPrice > service.price && (
                <Typography variant="h6" sx={{ textDecoration: 'line-through', color: '#999', fontSize: '1.2rem' }}>
                  {formatCurrency(service.originalPrice)}
                </Typography>
              )}
            </Box>

            {/* Included Services - Only show for combo services, placed right after price */}
            {service.isCombo && service.selectedServices && service.selectedServices.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem' }}>
                  Included Services
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  backgroundColor: '#f0f8ff', 
                  borderRadius: 2,
                  border: '1px solid #e3f2fd'
                }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {service.selectedServices.map((serviceName, index) => (
                      <Chip
                        key={index}
                        label={serviceName}
                        size="small"
                        sx={{
                          fontSize: '0.8rem',
                          height: 28,
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          border: '1px solid #bbdefb',
                          '& .MuiChip-label': {
                            px: 1.5
                          }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Description */}
            <Box sx={{ mb: 3, pb: 8 }}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem' }}>
                Description
              </Typography>
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 2,
                minHeight: 100,
                maxHeight: 200,
                overflowY: 'auto'
              }}>
              <Typography variant="body1" sx={{ 
                lineHeight: 1.6, 
                fontSize: '0.9rem',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}>
                  {service.detailedDescription || service.description || 'No description available for this service.'}
              </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Bottom Action Bar */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 56, // Position above bottom navigation
          left: 0,
          right: 0,
          zIndex: 1000,
          p: 1,
          borderTop: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          px: 2,
          py: 1
        }}>
            <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                  {formatCurrency(service.price)}
                </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {service.duration} min
              </Typography>
            </Box>
            </Box>
            
            {!cartItemExists ? (
              <Button
                variant="contained"
                size="medium"
                startIcon={<ShoppingCart />}
                onClick={handleAddToCart}
                sx={{
                  bgcolor: '#1976d2',
                  px: 3,
                  py: 1,
                  fontSize: '0.85rem',
                  minWidth: 'auto'
                }}
              >
                Add to Cart
              </Button>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  onClick={() => handleQuantityChange(quantity - 1)}
                  sx={{ bgcolor: 'primary.main', color: 'white', width: 24, height: 24 }}
                >
                  <Remove sx={{ fontSize: 12 }} />
                </IconButton>
                <Typography variant="body1" sx={{ minWidth: 20, textAlign: 'center', fontSize: '0.9rem', fontWeight: 'bold' }}>
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => handleQuantityChange(quantity + 1)}
                  sx={{ bgcolor: 'primary.main', color: 'white', width: 24, height: 24 }}
                >
                  <Add sx={{ fontSize: 12 }} />
                </IconButton>
                <Chip 
                  label={`In Cart (${cartQuantity})`} 
                  color="success" 
                  size="small"
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              </Box>
            )}
        </Box>
      </Paper>



      <BottomNavigation />

      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {service.name || service.serviceName} added to cart!
        </Alert>
      </Snackbar>

      {/* Guest Sign-up Prompt Dialog */}
      <Snackbar
        open={showSignUpPrompt}
        autoHideDuration={6000}
        onClose={() => setShowSignUpPrompt(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSignUpPrompt(false)}
          severity="info"
          sx={{ width: '100%' }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setShowSignUpPrompt(false);
                navigate('/signup');
              }}
            >
              Sign Up
            </Button>
          }
        >
          Please sign up to {restrictedFeature}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceDetails; 